import React, { useId } from 'react';
import { Lock, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type {
  AdditionalRecipient,
  EncompassRoleOption
} from '@/services/notificationSettingsService';
import type {
  LockedRecipientDescriptor,
  NotificationRecipientContractEntry,
  RecipientTargetKind
} from '../../../../supabase/functions/_shared/notification-recipient-contract';
import {
  isSameTarget,
  type RecipientConfigV1,
  type RecipientTarget,
  type RecipientZone
} from './types';
import { getTargetLabel } from './config';
import { RecipientPicker } from './RecipientPicker';

interface RecipientZonesProps {
  config: RecipientConfigV1;
  contract: NotificationRecipientContractEntry;
  availableUsers: AdditionalRecipient[];
  userRoles: string[];
  encompassRoles: EncompassRoleOption[];
  encompassRolesError?: string | null;
  usingCodeDefaults?: boolean;
  disabled?: boolean;
  onChange: (config: RecipientConfigV1) => void;
}

function chipTone(target: RecipientTarget): string {
  switch (target.kind) {
    case 'user':
    case 'raw_email':
      return 'individual';
    case 'user_role':
      return 'user-role';
    case 'loan_role':
    case 'encompass_loan_role':
      return 'loan-role';
    case 'loan_party':
      return 'loan-party';
    case 'loan_contact':
      return 'loan-contact';
  }
}

function dotColor(target: RecipientTarget): string {
  switch (target.kind) {
    case 'user':
    case 'raw_email':
      return '#0f766e';
    case 'user_role':
      return '#1d4ed8';
    case 'loan_role':
    case 'encompass_loan_role':
      return '#0f766e';
    case 'loan_party':
      return '#b45309';
    case 'loan_contact':
      return '#0891b2';
  }
}

function removeMatching(
  targets: RecipientTarget[],
  target: RecipientTarget
): RecipientTarget[] {
  let removed = false;
  return targets.filter((candidate) => {
    if (!removed && isSameTarget(candidate, target)) {
      removed = true;
      return false;
    }
    return true;
  });
}

function recipientCountLabel(
  to: RecipientTarget[],
  cc: RecipientTarget[],
  contract: NotificationRecipientContractEntry,
  usingCodeDefaults: boolean
): string {
  const total = to.length + cc.length;
  if (
    usingCodeDefaults
    && contract.primaryMode === 'code_default_until_overridden'
  ) {
    return 'Code-default routing shown';
  }
  const noun = contract.routingMode === 'required_plus_additions'
    ? 'configured addition'
    : 'configured recipient';
  return `${total} ${noun}${total === 1 ? '' : 's'}`;
}

export function RecipientZones({
  config,
  contract,
  availableUsers,
  userRoles,
  encompassRoles,
  encompassRolesError,
  usingCodeDefaults,
  disabled,
  onChange
}: RecipientZonesProps) {
  const groupId = useId();
  const userLabels = new Map(
    availableUsers.map((user) => [
      user.id,
      `${user.full_name} <${user.email}>`
    ])
  );

  const addTarget = (zone: RecipientZone, target: RecipientTarget) => {
    const to = removeMatching(config.to, target);
    const cc = removeMatching(config.cc, target);
    onChange({
      ...config,
      to: zone === 'to' ? [...to, target] : to,
      cc: zone === 'cc' ? [...cc, target] : cc
    });
  };

  const removeTarget = (zone: RecipientZone, target: RecipientTarget) => {
    onChange({
      ...config,
      [zone]: removeMatching(config[zone], target)
    });
  };

  const renderConfiguredChip = (
    target: RecipientTarget,
    zone: RecipientZone,
    index: number
  ) => {
    const targetLabel = getTargetLabel(
      target,
      target.kind === 'user' ? userLabels.get(target.user_id) : undefined
    );

    return (
      <span
        key={`${zone}-${target.kind}-${index}`}
        className={`en-role-chip ${chipTone(target)}`}
      >
        <span
          className="en-role-dot"
          style={{ backgroundColor: dotColor(target) }}
        />
        <span className="truncate">{targetLabel}</span>
        {!disabled && (
          <button
            type="button"
            className="en-chip-x"
            onClick={() => removeTarget(zone, target)}
            aria-label={`Remove ${targetLabel}`}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </span>
    );
  };

  const renderLockedZone = (
    zone: RecipientZone,
    descriptors: readonly LockedRecipientDescriptor[]
  ) => {
    if (descriptors.length === 0) {
      return null;
    }
    const labelId = `${groupId}-automatic-${zone}`;
    return (
      <div className="en-zone en-zone-automatic" role="group" aria-labelledby={labelId}>
        <div className="en-zone-head">
          <div className="flex items-center gap-2">
            <span id={labelId} className="en-zone-label">
              Automatic {zone === 'to' ? 'To' : 'CC'}
            </span>
            <Badge variant="secondary" className="gap-1">
              <Lock className="h-3 w-3" aria-hidden="true" />
              Required
            </Badge>
          </div>
        </div>
        <div className="en-chips">
          {descriptors.map((descriptor) => (
            <span
              key={`${zone}-${descriptor.source}`}
              className="en-role-chip en-role-chip-locked"
              title={descriptor.source}
            >
              <span className="en-role-dot" />
              <span className="truncate">{descriptor.label}</span>
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderConfiguredZone = (
    zone: RecipientZone,
    allowedKinds: readonly RecipientTargetKind[]
  ) => {
    if (allowedKinds.length === 0) {
      return null;
    }
    const requiredPlusAdditions = contract.routingMode === 'required_plus_additions';
    const label = requiredPlusAdditions
      ? `Additional ${zone === 'to' ? 'To' : 'CC'}`
      : zone === 'to' ? 'To' : 'CC';
    const labelId = `${groupId}-${zone}`;
    const targets = config[zone];

    return (
      <div className="en-zone" role="group" aria-labelledby={labelId}>
        <div className="en-zone-head">
          <span id={labelId} className="en-zone-label">{label}</span>
          <RecipientPicker
            availableUsers={availableUsers}
            userRoles={userRoles}
            encompassRoles={encompassRoles}
            allowedKinds={allowedKinds}
            unavailableTargets={config[zone]}
            disabled={disabled}
            onAdd={(target) => addTarget(zone, target)}
          />
        </div>
        <div className="en-chips">
          {targets.length === 0 ? (
            <span className="en-empty-zone">
              {requiredPlusAdditions
                ? 'No additional recipients configured.'
                : 'No configured recipients.'}
            </span>
          ) : (
            targets.map((target, index) =>
              renderConfiguredChip(target, zone, index)
            )
          )}
        </div>
      </div>
    );
  };

  const allowsEncompassRoles = [
    ...contract.allowedToKinds,
    ...contract.allowedCcKinds
  ].includes('encompass_loan_role');

  return (
    <div className="en-recipient-zones">
      <div className="en-section-title mb-0">
        <span className="sr-only">Recipient summary</span>
        <span className="en-hint">
          {recipientCountLabel(
            config.to,
            config.cc,
            contract,
            Boolean(usingCodeDefaults)
          )}
          {contract.loanScope === 'single' && allowsEncompassRoles
            ? ' · Encompass roles resolve on the triggering loan'
            : ''}
        </span>
      </div>

      {usingCodeDefaults
        && contract.primaryMode === 'code_default_until_overridden'
        && contract.lockedTo.length === 0
        && config.to.length === 0 && (
          <div className="en-zone en-zone-default" role="group" aria-label="Current code default To">
            <div className="en-zone-head">
              <div className="flex items-center gap-2">
                <span className="en-zone-label">To</span>
                <Badge variant="outline">Code default</Badge>
              </div>
            </div>
            <div className="en-chips">
              <span className="en-role-chip">
                <span className="en-role-dot" />
                <span className="truncate">{contract.defaultSummary}</span>
              </span>
            </div>
          </div>
      )}

      {renderLockedZone('to', contract.lockedTo)}
      {renderConfiguredZone('to', contract.allowedToKinds)}
      {renderLockedZone('cc', contract.lockedCc)}
      {renderConfiguredZone('cc', contract.allowedCcKinds)}

      {allowsEncompassRoles && encompassRolesError && (
        <p className="en-role-catalog-error" role="status">
          Encompass roles are temporarily unavailable. Existing routing remains unchanged.
        </p>
      )}
      {allowsEncompassRoles && !encompassRolesError && encompassRoles.length === 0 && (
        <p className="en-role-catalog-error" role="status">
          No selectable Encompass roles have been synchronized for this organization.
        </p>
      )}
    </div>
  );
}
